import { Close, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useCallback, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import service from "../../services/voucher";
import eventService from "../../services/event";
import config from "../../utils/config";
import { rupiah } from "../../utils/currency";
import queryDefault from "../../utils/queryDefault";
import cookie from "../../utils/useCookie";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function Voucher() {
  const [pageSize, setPageSize] = useState<number>(10);
  const user = JSON.parse(cookie.getCookie("USER") || "");
  const [options, setOptions] = useState<any>({
    keyword: "",
    event_id: user.event_id || "",
  });
  const [selectedEventId, setSelectedEventId] = useState<string>(user.event_id || "");
  const alert = useAlert();
  const notification = useNotification();
  const listVoucher = useQuery(
    ["voucher-list", options],
    ({ queryKey }) => {
      return service.getAllVoucher({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const listEvent = useQuery(
    ["events"],
    () => {
      return eventService.getAllEvent({ keyword: "" });
    },
    {
      ...queryDefault,
    }
  );

  const voucherStatistics = useQuery(
    ["voucher-statistics", selectedEventId],
    () => {
      return service.getVoucherStatistics(
        selectedEventId ? { event_id: selectedEventId } : {}
      );
    },
    {
      ...queryDefault,
    }
  );

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });
  const deleteVoucher = useMutation("delete-voucher", service.deleteVoucher, {
    onSuccess: () => {
      listVoucher.refetch();
      voucherStatistics.refetch();
      notification.onOpen({
        message: "Voucher was successfully deleted!",
        type: "success",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const downloadTemplate = useMutation(
    "download-voucher-template",
    service.downloadTemplate,
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Voucher template downloaded!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to download template",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const uploadVoucher = useMutation(
    "upload-voucher",
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return service.uploadExcel(formData);
    },
    {
      onSuccess: (res: any) => {
        listVoucher.refetch();
        voucherStatistics.refetch();
        notification.onOpen({
          message: get(res, "message", "Voucher uploaded successfully"),
          type: "success",
          position: "top",
        });
      },
      onError: (err: any) => {
        notification.onOpen({
          message: get(err, "response.data.message", "Upload failed"),
          type: "error",
          position: "top",
        });
      },
    }
  );

  const downloadReport = useMutation(
    "download-voucher-report",
    (params?: any) => service.downloadReport(params),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Voucher report downloaded!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to download report",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const onDelete = (id: number) => {
    alert.onClose();
    deleteVoucher.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this voucher?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const onHandleSearch = useCallback((values: { keyword: any }) => {
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  const handleEventFilter = (eventId: string) => {
    setSelectedEventId(eventId);
    setOptions((prev: any) => ({
      ...prev,
      event_id: eventId,
    }));
  };

  // Get statistics data
  const statsData = get(voucherStatistics, "data.data", {});
  const overallStats = get(statsData, "overall", {});
  const eventStats = get(statsData, "by_event", []);
  const topVouchers = get(statsData, "top_vouchers", []);
  
  return (
    <MainLayout>
      <Helmet>
        <title>Voucher List</title>
      </Helmet>

      <Breadcrumb
        label="Voucher List"
        breadcrumbs={[{ label: "Voucher Management", href: "/admin/voucher" }]}
      />

      {/* Statistics Cards */}
      <ProtectedPermissionPage acceptPermissions={[1,3]}>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Vouchers
                </Typography>
                <Typography variant="h4">
                  {get(overallStats, "total_vouchers", 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Used
                </Typography>
                <Typography variant="h4" color="success.main">
                  {get(overallStats, "total_used", 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Vouchers
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {get(overallStats, "active_vouchers", 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Expired Vouchers
                </Typography>
                <Typography variant="h4" color="error.main">
                  {get(overallStats, "expired_vouchers", 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        {eventStats.length > 0 && (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Voucher Usage by Event
                  </Typography>
                  <BarChart
                    width={500}
                    height={300}
                    series={[
                      {
                        data: eventStats.map((event: any) => event.total_used),
                        label: "Used",
                        color: "#1976d2",
                      },
                      {
                        data: eventStats.map((event: any) => event.remaining),
                        label: "Remaining",
                        color: "#ff9800",
                      },
                    ]}
                    xAxis={[
                      {
                        data: eventStats.map((event: any) => event.event_name),
                        scaleType: "band",
                      },
                    ]}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Usage Distribution
                  </Typography>
                  <PieChart
                    width={400}
                    height={300}
                    series={[
                      {
                        data: [
                          {
                            id: 0,
                            value: get(overallStats, "total_used", 0),
                            label: "Used",
                            color: "#4caf50",
                          },
                          {
                            id: 1,
                            value: get(overallStats, "total_booked", 0),
                            label: "Booked",
                            color: "#ff9800",
                          },
                          {
                            id: 2,
                            value: get(overallStats, "total_remaining", 0),
                            label: "Remaining",
                            color: "#2196f3",
                          },
                        ],
                      },
                    ]}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Search and Filter Section */}
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Event</InputLabel>
              <Select
                value={selectedEventId}
                label="Filter by Event"
                onChange={(e) => handleEventFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="">
                  <em>All Events</em>
                </MenuItem>
                {get(listEvent, ["data", "data"], []).map((event: any) => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Controller
              name="keyword"
              control={form.control}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Input Keyword here"
                  {...field}
                  {...config.onEnter(() => {
                    form.handleSubmit(onHandleSearch)();
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        onClick={() => {
                          if (field.value) {
                            field.onChange("");
                            setOptions((prev: any) => ({
                              ...prev,
                              keyword: "",
                            }));
                          }
                        }}
                      >
                        {field.value ? (
                          <Close color="error" sx={{ cursor: "pointer" }} />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Button
              variant="outlined"
              onClick={form.handleSubmit(onHandleSearch)}
            >
              SEARCH
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1}>
          <ProtectedPermissionPage acceptPermissions={[1,3]}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/admin/voucher/create-voucher"
            >
              ADD VOUCHER
            </Button>
          </ProtectedPermissionPage>

          <ProtectedPermissionPage acceptPermissions={[1,3]}>
            <Button
              variant="outlined"
              onClick={() => downloadTemplate.mutate()}
              disabled={downloadTemplate.isLoading}
            >
              {downloadTemplate.isLoading ? "Downloading..." : "DOWNLOAD TEMPLATE"}
            </Button>
          </ProtectedPermissionPage>

          <ProtectedPermissionPage acceptPermissions={[1,3]}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadReport.mutate(selectedEventId ? { event_id: selectedEventId } : {})}
              disabled={downloadReport.isLoading}
            >
              {downloadReport.isLoading ? "Downloading..." : "DOWNLOAD REPORT"}
            </Button>
          </ProtectedPermissionPage>

          <ProtectedPermissionPage acceptPermissions={[1,3]}>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={uploadInputRef}
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  uploadVoucher.mutate(file);
                  // reset value to allow uploading same file again
                  e.currentTarget.value = "";
                }
              }}
            />
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploadVoucher.isLoading}
            >
              {uploadVoucher.isLoading ? "Uploading..." : "UPLOAD VOUCHER"}
            </Button>
          </ProtectedPermissionPage>
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>
        <DataGrid
          autoHeight
          loading={listVoucher.isLoading}
          getRowId={(record) => get(record, "id")}
          rows={get(listVoucher, "data.data", [])}
          rowCount={get(listVoucher, "data.data", []).length}
          page={options.page_number}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 20, 30]}
          pagination
          disableSelectionOnClick
          onPageChange={(newPage) => {
            setOptions((prev: any) => ({ ...prev, page_number: newPage }));
          }}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          components={{
            NoRowsOverlay: CustomNoRowOverlay,
          }}
          columns={[
            {
              field: "event_name",
              headerName: "Event",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "code",
              headerName: "Voucher Code",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "available",
              headerName: "Total Voucher",
              flex: 1,
              filterable: false,
            },
            {
              field: "booked",
              headerName: "Booked",
              flex: 1,
              filterable: false,
            },
            {
              field: "used",
              headerName: "Used",
              flex: 1,
              filterable: false,
            },
            {
              field: "remaining",
              headerName: "Remaining",
              flex: 1,
              filterable: false,
              renderCell(params) {
                  return <>{params.row.available - (params.row.booked + params.row.used)}</>
              },
            },
            {
              field: "amount",
              headerName: "Amount",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) => {
                return (
                  <>
                    {params.row.is_persen
                      ? `${params.value} %`
                      : rupiah(params.value)}
                  </>
                );
              },
            },
            {
              field: "max_amount",
              headerName: "Max Amount",
              flex: 1,
              filterable: false,
              minWidth: 150,
              renderCell: (params) => {
                return rupiah(params.value);
              },
            },
            {
              field: "actions",
              headerName: "Action",
              sortable: false,
              filterable: false,
              width: 200,
              renderCell: (params) => {
                return (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      component={RouterLink}
                      to={`/admin/voucher/edit/${get(params, "row.id")}`}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => onClickDelete(params.row.id)}
                    >
                      Delete
                    </Button>
                  </Stack>
                );
              },
            },
          ]}
          sx={{
            ".MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
            },
          }}
        />
      </Box>
    </MainLayout>
  );
}
